/**
 * Combobox — accessible searchable select built on `cmdk` + Radix
 * Popover.
 *
 * Bridges the gap between a {@link CommandPalette} (modal, app-wide
 * launcher) and a Radix Select (in-form picker with a fixed option
 * list). The Combobox stays inline, lets the user type to filter the
 * options, and emits the selected option's `value` through a typed
 * callback.
 *
 * Single responsibility: rendering the trigger + listbox + filtering
 * lifecycle. The host owns the selection state so the picker stays
 * fully controllable.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useCallback, useMemo, useState } from "react";
import { Command } from "cmdk";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { GRAY, PRIMARY } from "../../design-system/tokens.js";
import { cn } from "../../styling/index.js";

export interface ComboboxOption {
  /** Stable identifier — also the value emitted on selection. */
  readonly value: string;
  /** Label rendered inside the listbox row. */
  readonly label: string;
  /** Optional fuzzy-search keywords. */
  readonly keywords?: readonly string[];
  /** Optional inline hint rendered to the right of the label. */
  readonly hint?: React.ReactNode;
}

export interface ComboboxProps {
  /** Available options. */
  readonly options: readonly ComboboxOption[];
  /** Selected option's `value`, or undefined when nothing is selected. */
  readonly value?: string;
  /** Selection handler. */
  readonly onChange: (value: string) => void;
  /** Placeholder rendered inside the trigger when nothing is selected. */
  readonly placeholder?: string;
  /** Search-input placeholder. Defaults to "Search…". */
  readonly searchPlaceholder?: string;
  /** Copy shown when no rows match. Defaults to "No matches". */
  readonly emptyLabel?: string;
  /** Accessible label for the combobox trigger. */
  readonly ariaLabel: string;
  /** Optional className applied to the trigger. */
  readonly className?: string;
}

const TRIGGER_STYLE: React.CSSProperties = {
  alignItems: "center",
  background: `var(--csk-combobox-trigger-bg, white)`,
  border: `1px solid var(--csk-combobox-border, ${GRAY[300]})`,
  borderRadius: "var(--csk-combobox-radius, 0.375rem)",
  color: `var(--csk-combobox-text, ${GRAY[900]})`,
  cursor: "pointer",
  display: "inline-flex",
  fontSize: "0.875rem",
  gap: "0.5rem",
  justifyContent: "space-between",
  minWidth: "12rem",
  padding: "0.45rem 0.65rem",
  boxShadow: "var(--csk-combobox-trigger-shadow, none)",
};

const POPOVER_STYLE: React.CSSProperties = {
  background: `var(--csk-combobox-popover-bg, white)`,
  border: `1px solid var(--csk-combobox-border, ${GRAY[300]})`,
  borderRadius: "var(--csk-combobox-popover-radius, 0.5rem)",
  boxShadow: "var(--csk-combobox-popover-shadow, 0 18px 36px rgba(15, 23, 42, 0.12))",
  color: `var(--csk-combobox-text, ${GRAY[900]})`,
  display: "grid",
  gap: "0.5rem",
  padding: "0.5rem",
  width: "var(--csk-combobox-width, 18rem)",
};

const INPUT_STYLE: React.CSSProperties = {
  background: `var(--csk-combobox-input-bg, white)`,
  border: `1px solid var(--csk-combobox-border, ${GRAY[300]})`,
  borderRadius: "var(--csk-combobox-radius, 0.375rem)",
  color: `var(--csk-combobox-text, ${GRAY[900]})`,
  fontSize: "0.875rem",
  outline: "none",
  padding: "0.4rem 0.55rem",
  width: "100%",
};

const LIST_STYLE: React.CSSProperties = {
  display: "grid",
  gap: "0.1rem",
  maxHeight: "16rem",
  overflowY: "auto",
};

const ITEM_STYLE: React.CSSProperties = {
  alignItems: "center",
  borderRadius: "0.375rem",
  cursor: "pointer",
  display: "flex",
  fontSize: "0.875rem",
  gap: "0.5rem",
  justifyContent: "space-between",
  padding: "0.4rem 0.5rem",
};

/** Renders an inline, accessible searchable select. */
export const Combobox = React.memo(function Combobox({
  options,
  value,
  onChange,
  placeholder = "Select…",
  searchPlaceholder = "Search…",
  emptyLabel = "No matches",
  ariaLabel,
  className,
}: ComboboxProps): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const selected = useMemo(
    () => options.find((option) => option.value === value),
    [options, value],
  );

  const handleSelect = useCallback(
    (next: string) => {
      onChange(next);
      setOpen(false);
    },
    [onChange],
  );

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
      <PopoverPrimitive.Trigger asChild>
        <button
          type="button"
          aria-label={ariaLabel}
          aria-haspopup="listbox"
          aria-expanded={open}
          data-csk-combobox-trigger
          className={cn("font-body", className)}
          style={TRIGGER_STYLE}
        >
          <span
            style={{
              color:
                selected !== undefined
                  ? `var(--csk-combobox-text, ${GRAY[900]})`
                  : `var(--csk-combobox-muted, ${GRAY[600]})`,
            }}
          >
            {selected?.label ?? placeholder}
          </span>
          <span aria-hidden="true" style={{ color: `var(--csk-combobox-muted, ${GRAY[600]})` }}>
            ▾
          </span>
        </button>
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={6}
          style={POPOVER_STYLE}
          data-csk-combobox-content
        >
          <style>{`
            [cmdk-input]::placeholder { color: var(--csk-combobox-muted, ${GRAY[600]}); }
            [cmdk-item] { color: var(--csk-combobox-text, ${GRAY[900]}); }
            [cmdk-item][data-selected="true"] {
              background: var(--csk-combobox-selected-bg, ${PRIMARY[700]});
              color: var(--csk-combobox-selected-text, white) !important;
            }
            [cmdk-item][data-selected="true"] * { color: var(--csk-combobox-selected-text, white) !important; }
          `}</style>
          <Command label={ariaLabel} shouldFilter>
            <Command.Input style={INPUT_STYLE} placeholder={searchPlaceholder} autoFocus />
            <Command.List style={LIST_STYLE}>
              <Command.Empty
                style={{
                  color: `var(--csk-combobox-muted, ${GRAY[600]})`,
                  fontSize: "0.8125rem",
                  padding: "0.5rem",
                }}
              >
                {emptyLabel}
              </Command.Empty>
              {options.map((option) => (
                <Command.Item
                  key={option.value}
                  value={`${option.label} ${(option.keywords ?? []).join(" ")}`}
                  onSelect={() => {
                    handleSelect(option.value);
                  }}
                  style={ITEM_STYLE}
                >
                  <span>{option.label}</span>
                  {option.hint !== undefined && (
                    <span
                      style={{
                        color: `var(--csk-combobox-hint, ${PRIMARY[700]})`,
                        fontSize: "0.75rem",
                      }}
                    >
                      {option.hint}
                    </span>
                  )}
                </Command.Item>
              ))}
            </Command.List>
          </Command>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
});
